import { useState } from 'react';
import { Edit2, Save, X, Upload, Trash2, Type, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FONT_OPTIONS = [
  'Plus Jakarta Sans', 'Manrope', 'Inter', 'Poppins', 'DM Sans',
  'Sora', 'Space Grotesk', 'Outfit', 'Montserrat', 'Raleway',
  'Roboto', 'Open Sans', 'Lato', 'Nunito', 'Work Sans'
];

const SIZE_OPTIONS = [
  { label: 'XS', value: 'text-xs' },
  { label: 'SM', value: 'text-sm' },
  { label: 'Base', value: 'text-base' },
  { label: 'LG', value: 'text-lg' },
  { label: 'XL', value: 'text-xl' },
  { label: '2XL', value: 'text-2xl' },
  { label: '3XL', value: 'text-3xl' },
  { label: '4XL', value: 'text-4xl' },
  { label: '5XL', value: 'text-5xl' },
  { label: '6XL', value: 'text-6xl' },
];

const COLOR_PRESETS = [
  '#ffffff', '#f8fafc', '#94a3b8', '#64748b', '#334155', '#0f172a', '#000000',
  '#22c55e', '#16a34a', '#059669', '#10b981', '#14b8a6',
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899',
  '#ef4444', '#f97316', '#eab308', '#84cc16',
];

function StyleToolbar({ style, onChange }) {
  const s = style || {};
  return (
    <div className="flex flex-wrap items-center gap-2 p-2 bg-background/50 rounded-lg border border-border/30 mt-1">
      <select value={s.fontFamily || ''} onChange={(e) => onChange({ ...s, fontFamily: e.target.value })}
        className="h-7 px-2 text-xs bg-card border border-border/40 rounded text-foreground focus:ring-1 focus:ring-primary">
        <option value="">Default Font</option>
        {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
      </select>
      <select value={s.fontSize || ''} onChange={(e) => onChange({ ...s, fontSize: e.target.value })}
        className="h-7 px-2 text-xs bg-card border border-border/40 rounded text-foreground focus:ring-1 focus:ring-primary">
        <option value="">Default Size</option>
        {SIZE_OPTIONS.map(sz => <option key={sz.value} value={sz.value}>{sz.label}</option>)}
      </select>
      <div className="flex items-center gap-1">
        <Palette className="w-3 h-3 text-muted-foreground" />
        <input type="color" value={s.color || '#ffffff'} onChange={(e) => onChange({ ...s, color: e.target.value })}
          className="w-6 h-6 rounded cursor-pointer border border-border/40" />
        <div className="flex gap-0.5">
          {COLOR_PRESETS.slice(0, 7).map(c => (
            <button key={c} onClick={() => onChange({ ...s, color: c })}
              className="w-4 h-4 rounded-full border border-border/40 hover:scale-125 transition-transform"
              style={{ background: c }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ImageField({ value, onChange, label, uploading, onUpload }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-foreground">{label}</Label>
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" className="bg-background border-border/40 text-sm h-8" />
      <div className="flex items-center gap-2">
        <div className="border border-dashed border-border/60 rounded-lg p-2 text-center hover:border-primary/50 transition-colors flex-1">
          <input type="file" accept="image/*" onChange={onUpload} className="hidden" id={`img-${label}`} disabled={uploading} />
          <label htmlFor={`img-${label}`} className="cursor-pointer flex items-center justify-center gap-1 text-xs text-muted-foreground">
            {uploading ? <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin"></div> : <Upload className="w-3 h-3" />}
            {uploading ? 'Uploading...' : 'Upload'}
          </label>
        </div>
        {value && (
          <>
            <img src={value} alt="" className="w-10 h-10 object-cover rounded border border-border/40" />
            <button onClick={() => onChange('')} className="text-xs text-destructive hover:underline"><Trash2 className="w-3 h-3" /></button>
          </>
        )}
      </div>
    </div>
  );
}

export default function SectionEditor({ user, sectionTitle, fields, content, pageName, sectionKey, onSave }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);

  if (!user || user.role !== 'admin') return null;

  const handleOpen = () => {
    setData(JSON.parse(JSON.stringify(content || {})));
    setOpen(true);
  };

  const updateField = (key, value) => setData(prev => ({ ...prev, [key]: value }));
  const updateStyle = (key, style) => setData(prev => ({ ...prev, [`${key}Style`]: style }));

  const handleUpload = async (event, fieldKey) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingField(fieldKey);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const endpoint = token ? `${API}/upload` : `${API}/upload-public`;
      const res = await axios.post(endpoint, fd, { headers });
      const url = res.data.url.startsWith('http') ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      updateField(fieldKey, url);
      toast.success('Uploaded!');
    } catch (error) { console.error('Upload failed:', error); toast.error('Upload failed'); }
    finally { setUploadingField(null); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const fullPageRes = await axios.get(`${API}/pages/${pageName}`);
      const fullContent = fullPageRes.data?.content || {};
      fullContent[sectionKey] = data;
      await axios.put(`${API}/pages/${pageName}`, { content: fullContent }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Section saved!');
      if (onSave) onSave(data);
      setOpen(false);
    } catch (error) { console.error('Save failed:', error); toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button onClick={handleOpen}
        className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-primary/90 hover:bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:shadow-xl transition-all"
        title={`Edit ${sectionTitle}`}
        data-testid={`edit-section-${sectionKey}`}>
        <Edit2 className="w-3.5 h-3.5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col bg-card border-border/40">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
              <Type className="w-5 h-5 text-primary" /> Edit: {sectionTitle}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Customize text, fonts, colors, and images for this section.
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 space-y-4 py-4 px-1">
            {fields.map((field) => {
              if (field.type === 'text' || field.type === 'title') {
                return (
                  <div key={field.key} className="space-y-1">
                    <Label className="text-xs font-medium text-foreground capitalize">{field.label}</Label>
                    {field.multiline ? (
                      <Textarea value={data[field.key] || ''} onChange={(e) => updateField(field.key, e.target.value)}
                        rows={3} className="bg-background border-border/40 text-foreground text-sm" />
                    ) : (
                      <Input value={data[field.key] || ''} onChange={(e) => updateField(field.key, e.target.value)}
                        className="bg-background border-border/40 text-foreground text-sm h-9" />
                    )}
                    {field.styled && <StyleToolbar style={data[`${field.key}Style`]} onChange={(s) => updateStyle(field.key, s)} />}
                  </div>
                );
              }
              if (field.type === 'image') {
                return (
                  <ImageField key={field.key} value={data[field.key]} label={field.label}
                    onChange={(v) => updateField(field.key, v)}
                    uploading={uploadingField === field.key}
                    onUpload={(e) => handleUpload(e, field.key)} />
                );
              }
              if (field.type === 'video') {
                return (
                  <div key={field.key} className="space-y-1">
                    <Label className="text-xs font-medium text-foreground">{field.label}</Label>
                    <Input value={data[field.key] || ''} onChange={(e) => updateField(field.key, e.target.value)}
                      placeholder="Video URL" className="bg-background border-border/40 text-sm h-8" />
                    {data[field.key] && (
                      <button onClick={() => updateField(field.key, '')} className="text-xs text-destructive hover:underline">Remove video</button>
                    )}
                  </div>
                );
              }
              if (field.type === 'list') {
                const items = data[field.key] || [];
                return (
                  <div key={field.key} className="space-y-2">
                    <Label className="text-xs font-medium text-foreground">{field.label}</Label>
                    {items.map((item, idx) => (
                      <div key={idx} className="flex gap-2 items-start p-2 bg-background/50 rounded border border-border/20">
                        <div className="flex-1 space-y-1">
                          {field.itemFields.map(sf => (
                            <div key={sf.key}>
                              <Label className="text-[10px] text-muted-foreground">{sf.label}</Label>
                              {sf.type === 'image' ? (
                                <ImageField value={item[sf.key]} label={sf.label}
                                  onChange={(v) => { const a = [...items]; a[idx] = { ...item, [sf.key]: v }; updateField(field.key, a); }}
                                  uploading={uploadingField === `${field.key}-${idx}-${sf.key}`}
                                  onUpload={async (e) => {
                                    const file = e.target.files?.[0]; if (!file) return;
                                    setUploadingField(`${field.key}-${idx}-${sf.key}`);
                                    try {
                                      const fd = new FormData(); fd.append('file', file);
                                      const token = localStorage.getItem('token');
                                      const res = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
                                      const url = res.data.url.startsWith('http') ? res.data.url : `${BACKEND_URL}${res.data.url}`;
                                      const a = [...items]; a[idx] = { ...item, [sf.key]: url }; updateField(field.key, a);
                                      toast.success('Uploaded!');
                                    } catch (error) { console.error('Upload failed:', error); toast.error('Upload failed'); }
                                    finally { setUploadingField(null); }
                                  }} />
                              ) : (
                                <Input value={item[sf.key] || ''} onChange={(e) => { const a = [...items]; a[idx] = { ...item, [sf.key]: e.target.value }; updateField(field.key, a); }}
                                  className="bg-card border-border/40 text-sm h-7" />
                              )}
                            </div>
                          ))}
                        </div>
                        <button onClick={() => updateField(field.key, items.filter((_, i) => i !== idx))} className="text-destructive mt-1"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => {
                      const newItem = {};
                      field.itemFields.forEach(sf => { newItem[sf.key] = ''; });
                      updateField(field.key, [...items, newItem]);
                    }} className="border-border/40 text-xs">+ Add</Button>
                  </div>
                );
              }
              if (field.type === 'color') {
                return (
                  <div key={field.key} className="space-y-1">
                    <Label className="text-xs font-medium text-foreground">{field.label}</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={data[field.key] || '#22c55e'} onChange={(e) => updateField(field.key, e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border border-border/40" />
                      <Input value={data[field.key] || ''} onChange={(e) => updateField(field.key, e.target.value)}
                        placeholder="#hex" className="bg-background border-border/40 text-sm h-8 w-28" />
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-border/40">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-border/40"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export { StyleToolbar, ImageField, FONT_OPTIONS, SIZE_OPTIONS, COLOR_PRESETS };
